import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Polyline, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { Route, MapPin, Clock, ExternalLink } from "lucide-react";

const pin = (n) => L.divIcon({
  className: "",
  html: `<div style="width:36px;height:36px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#D96B43;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center"><span style="transform:rotate(45deg);color:#fff;font-weight:700;font-size:15px">${n}</span></div>`,
  iconSize: [36, 36], iconAnchor: [18, 36], popupAnchor: [0, -34],
});

const time = (v) => new Date(v.scheduled_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

export default function RouteMap() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/helpers/me/today", { params: { tz_offset: new Date().getTimezoneOffset() } }).then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data || data.visits.length === 0) return null;

  const pinned = data.visits.filter((v) => v.lat != null && v.lng != null);
  const points = pinned.map((v) => [v.lat, v.lng]);
  const center = points[0] || (data.home.lat != null ? [data.home.lat, data.home.lng] : null);
  const gmaps = pinned.length ? `https://www.google.com/maps/dir/${data.home.lat != null ? `${data.home.lat},${data.home.lng}/` : ""}${pinned.map((v) => `${v.lat},${v.lng}`).join("/")}` : null;

  return (
    <section data-testid="route-map" className="mb-10">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2"><Route className="w-6 h-6 text-[#2E7D6B]" /><h2 className="text-2xl font-display font-bold text-slate-900">Today's route</h2><span className="text-slate-500">({data.visits.length} visit{data.visits.length === 1 ? "" : "s"})</span></div>
        {gmaps && <a data-testid="open-gmaps-link" href={gmaps} target="_blank" rel="noreferrer" className="flex items-center gap-2 bg-[#E8F4F1] hover:bg-[#d6ebe5] text-[#1F5F4F] font-semibold px-4 min-h-[44px] rounded-xl"><ExternalLink className="w-4 h-4" /> Open in Google Maps</a>}
      </div>
      <div className="grid lg:grid-cols-5 gap-4">
        {center && (
          <div className="lg:col-span-3 rounded-2xl overflow-hidden border-2 border-[#E2D9CE] h-[360px]" data-testid="route-map-canvas">
            <MapContainer center={center} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {pinned.map((v) => (
                <Marker key={v.id} position={[v.lat, v.lng]} icon={pin(data.visits.indexOf(v) + 1)}>
                  <Popup><b>{time(v)}</b> · {v.title}<br />{v.location}</Popup>
                </Marker>
              ))}
              {points.length > 1 && <Polyline positions={points} pathOptions={{ color: "#2E7D6B", weight: 4, dashArray: "8 8" }} />}
            </MapContainer>
          </div>
        )}
        <ol className={`${center ? "lg:col-span-2" : "lg:col-span-5"} space-y-2`}>
          {data.visits.map((v, i) => (
            <li key={v.id}>
              <Link to={`/requests/${v.id}`} data-testid={`route-stop-${v.id}`} className="flex items-center gap-3 bg-white border border-[#E2D9CE] hover:border-[#2E7D6B] rounded-xl p-3 transition-colors">
                <span className="w-9 h-9 rounded-full bg-[#D96B43] text-white font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{v.title}</p>
                  <p className="text-sm text-slate-600 flex items-center gap-1"><Clock className="w-4 h-4" /> {time(v)} · {v.seeker_name}</p>
                  <p className="text-sm text-slate-600 flex items-center gap-1 truncate"><MapPin className="w-4 h-4" /> {v.location}{v.lat == null && <span className="text-slate-400"> · not pinned</span>}</p>
                </div>
              </Link>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
